import { useEffect, useRef } from 'react'
import type { KeyboardEvent, MouseEvent } from 'react'
import { useStyletron } from 'baseui'
import useRailHoverLabel from '@/shared/components/RailHoverBubble/useRailHoverLabel'
import getFlyoutPlacement from '../getFlyoutPlacement'
import type { MenuFamilyView, MenuFlyoutControls } from '../types'

const CLOSE_DELAY_MS = 150
const OPEN_KEYS = ['ArrowRight', 'ArrowDown']
const CLOSE_KEYS = ['Escape', 'ArrowLeft']

type OpenedBy = 'hover' | 'press'

const focusableRows = (flyout: HTMLElement | null) =>
  Array.from(flyout?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])

const nextRowIndex = (key: string, current: number, count: number) => {
  if (key === 'Home') return 0
  if (key === 'End') return count - 1
  if (key === 'ArrowDown') return (current + 1) % count
  if (key === 'ArrowUp') return (current - 1 + count) % count
  return null
}

const useMenuFamily = (family: MenuFamilyView) => {
  const [, theme] = useStyletron()
  const { anchorRef, geometry, show, hide } = useRailHoverLabel<HTMLButtonElement>()
  const flyoutRef = useRef<HTMLDivElement>(null)
  const closeTimer = useRef<number | undefined>(undefined)
  const openedBy = useRef<OpenedBy | null>(null)
  const focusFirstRow = useRef(false)
  const isOpen = family.open && geometry !== null

  const cancelClose = () => window.clearTimeout(closeTimer.current)

  const open = (by: OpenedBy) => {
    cancelClose()
    if (!isOpen) openedBy.current = by
    show()
    family.onOpen()
  }

  const close = () => {
    cancelClose()
    openedBy.current = null
    focusFirstRow.current = false
    hide()
    family.onClose()
  }

  const scheduleClose = () => {
    cancelClose()
    if (flyoutRef.current?.contains(document.activeElement)) return
    closeTimer.current = window.setTimeout(close, CLOSE_DELAY_MS)
  }

  const closeToTrigger = () => {
    close()
    anchorRef.current?.focus()
  }

  const openOnFirstRow = () => {
    if (isOpen) {
      focusableRows(flyoutRef.current)[0]?.focus()
      return
    }
    focusFirstRow.current = true
    open('press')
  }

  const onTriggerClick = (event: MouseEvent) => {
    if (event.detail === 0) {
      openOnFirstRow()
      return
    }
    if (isOpen && openedBy.current === 'press') {
      close()
      return
    }
    open('press')
    openedBy.current = 'press'
  }

  const onTriggerKeyDown = (event: KeyboardEvent) => {
    if (OPEN_KEYS.includes(event.key)) {
      event.preventDefault()
      openOnFirstRow()
    }
    if (event.key === 'Escape') close()
  }

  const onFlyoutKeyDown = (event: KeyboardEvent) => {
    if (CLOSE_KEYS.includes(event.key)) {
      event.preventDefault()
      closeToTrigger()
      return
    }
    if (event.key === 'Tab') {
      closeToTrigger()
      return
    }

    const rows = focusableRows(flyoutRef.current)
    const index = nextRowIndex(event.key, rows.indexOf(document.activeElement as HTMLElement), rows.length)
    if (index === null) return
    event.preventDefault()
    rows[index]?.focus()
  }

  useEffect(() => {
    if (!isOpen || !focusFirstRow.current) return
    focusFirstRow.current = false
    focusableRows(flyoutRef.current)[0]?.focus()
  }, [isOpen])

  // The hover label drops its geometry on any scroll: the family closes with it.
  useEffect(() => {
    if (family.open && geometry === null) close()
  }, [family.open, geometry])

  useEffect(() => {
    if (!isOpen) return undefined

    const closeOnOutsidePointer = (event: PointerEvent) => {
      const target = event.target as Node
      const inside = anchorRef.current?.contains(target) || flyoutRef.current?.contains(target)
      if (!inside) close()
    }
    const closeOnEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') close()
    }

    document.addEventListener('pointerdown', closeOnOutsidePointer, true)
    document.addEventListener('keydown', closeOnEscape)
    window.addEventListener('resize', close)

    return () => {
      document.removeEventListener('pointerdown', closeOnOutsidePointer, true)
      document.removeEventListener('keydown', closeOnEscape)
      window.removeEventListener('resize', close)
    }
  }, [isOpen])

  useEffect(() => cancelClose, [])

  const openOnHover = () => open('hover')
  const flyoutId = `menu-flyout-${family.id}`
  const flyout: MenuFlyoutControls = {
    ref: flyoutRef,
    id: flyoutId,
    titleId: `${flyoutId}-title`,
    placement: geometry ? getFlyoutPlacement(geometry, document.documentElement.clientHeight) : null,
    onMouseEnter: openOnHover,
    onMouseLeave: scheduleClose,
    onKeyDown: onFlyoutKeyDown,
    onRowClick: close,
  }

  return {
    isOpen,
    iconProps: { size: 16, color: theme.colors.primary, title: '' },
    trigger: {
      ref: anchorRef,
      id: `menu-family-${family.id}`,
      ariaControls: isOpen ? flyoutId : undefined,
      onClick: onTriggerClick,
      onKeyDown: onTriggerKeyDown,
      onMouseEnter: openOnHover,
      onMouseLeave: scheduleClose,
    },
    flyout,
  }
}

export default useMenuFamily
